
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import Clickable from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useAccessibleSubmit from "@MasterBluePrint/Common/Hooks/useAccessibleSubmit";
import Msq from "@MasterBluePrint/Components/Msq";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@MasterBluePrint/Overrides/Components/SmallHeadingAndText";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";


interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: CartLineQuantityStyles;
    label?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
    cartlineContainer?: InjectableCss;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    editableQuantityTextField: {
        labelProps: {
            variant: "legend",
            css: css`
                font-size: .625rem;
                text-transform: uppercase;
                font-weight: 600;
            `,
        },
    },
    cartlineContainer: {
        css: css`
            text-transform: uppercase;
            label {
                text-transform: uppercase;
            }
        `,
    }
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => { },
    extendedStyles,
}) => {
    const localOnQtyOrderedChange = (value: string) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            return;
        }

        onQtyOrderedChange(inputQty);
    };
    const [productQty, setProductQty] = useState(cartLine?.qtyOrdered?.toString());
    const [multipleSaleQty, setMultipleSaleQty] = React.useState(Number(cartLine?.properties?.multipleSaleQty));
    const keyUpHandler = () => {
        if (multipleSaleQty && multipleSaleQty > 1) {
            document.querySelector('[data-test-selector="cartTotal_Checkout"]')?.removeAttribute("disabled");
            document.querySelector('[data-test-selector="cartTotal_SubmitQuote"]')?.removeAttribute("disabled");
            document
                .querySelector(`[data-test-selector="addtolist_${cartLine.id}"]`)
                ?.setAttribute("style", "display:block");
        }
    };
    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );
    useEffect(() => {
        if (cartLine.qtyOrdered === Number(productQty)) {
            return;
        }

        setProductQty(`${cartLine.qtyOrdered || ""}`);
        const possibleNumber = Number(cartLine?.qtyOrdered);
        onQtyOrderedChange!(possibleNumber);
    }, [cartLine.qtyOrdered]);


    const increaseOrderedProductQty = () => {

        const amount = Number(productQty) + 1;

        setProductQty(amount.toString());

        if (!onQtyOrderedChange) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for`);
            return;
        }

        const possibleNumber = Number(amount);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChange(possibleNumber);
        }
    };
    const decreaseOrderedProductQty = () => {
        const amount = Number(productQty) > 1 ? Number(productQty) - 1 : Number(productQty);

        setProductQty(amount.toString());

        if (!onQtyOrderedChange) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for`);
            return;
        }

        const possibleNumber = Number(amount);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChange(possibleNumber);
        }
    };

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === ".") {
            event.preventDefault();
        }
    };
    const [isQuoteCart, setIsQuoteCart] = React.useState(false);
    useEffect(()=>{
        if(cart.properties?.isquote === "true")
        {
            setIsQuoteCart(true);
        }
    },[cart]);
    const [styles] = React.useState(() => mergeToNew(cartLineQuantityStyles, extendedStyles));

    if (editable && !isOutOfStock(cartLine)) {
        return (
            <GridContainer gap={8} style={{ display: "block", width: "100%" }} className="qty-container">
                {multipleSaleQty && multipleSaleQty > 1 ? (
                    <GridItem width={12}>
                        <Msq multipleSaleQty={multipleSaleQty.toString()} />
                    </GridItem>
                ) : (
                    ""
                )}
                <GridItem width={12}>
                    <StyledWrapper style={{ position: "relative" }} className="input-container">
                        <TextField
                            {...styles.editableQuantityTextField}
                            type="number"
                            min={0}
                            max={999999}
                            label={label || translate("Quantity:")}
                            value={value}
                            disabled={
                                !cart.canModifyOrder ||
                                cartLine.isPromotionItem ||
                                cart.type === "Job" ||
                                isLoading[cart.id] ||
                                isQuoteCart
                            }
                            onChange={changeHandler}
                            onKeyDown={keyDownHandler}
                            onBlur={blurHandler}
                            onBlurCapture={keyUpHandler}
                            data-test-selector="cartline_qty"
                            onKeyPress={keyPressHandler}
                        />
                        <Clickable
                            style={{
                                position: "absolute",
                                top: "2.1rem",
                                left: ".1rem",
                                height: "38px",
                                background: "rgba(104, 172, 247, 8%)",
                                padding: "10px",
                                fontSize: "1.4rem",
                                color: "#002554",
                                borderBottomLeftRadius: "2",
                                borderTopLeftRadius: "2",
                            }}
                            onChange={changeHandler}
                            onKeyDown={keyDownHandler}
                            onBlur={blurHandler}
                            onBlurCapture={keyUpHandler}
                            onClick={() => decreaseOrderedProductQty()}
                            disabled={isQuoteCart}
                        >
                            <Icon src="Minus" />
                        </Clickable>

                        <Clickable
                            onChange={changeHandler}
                            onKeyDown={keyDownHandler}
                            onBlur={blurHandler}
                            onBlurCapture={keyUpHandler}
                            onClick={() => increaseOrderedProductQty()}
                            disabled={isQuoteCart}
                            style={{
                                position: "absolute",
                                top: "2.1rem",
                                right: ".08rem",
                                height: "38px",
                                background: "rgba(104, 172, 247, 8%)",
                                padding: "10px",
                                fontSize: "1.4rem",
                                color: "#002554",
                                borderBottomRightRadius: "2",
                                borderTopRightRadius: "2",
                            }}
                        >
                            <Icon src="Plus" />
                        </Clickable>
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        );
    }

    return (
        <GridContainer gap={8} style={{ display: "block", width: "100%" }}>
            <GridItem width={12}>
                <StyledWrapper>
                    {multipleSaleQty && multipleSaleQty > 1 ? <Msq multipleSaleQty={multipleSaleQty.toString()} /> : ""}
                </StyledWrapper>
            </GridItem>
            <GridItem width={12}>
                <StyledWrapper>
                    <SmallHeadingAndText
                        {...styles.readOnlyQuantityText}
                        heading={label || translate("Quantity:")}
                        text={cartLine.qtyOrdered!}
                    />
                </StyledWrapper>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
